<template>
  <v-container fluid grid-list-xs fill-height>
    <v-layout column warp>
      <template>
        <v-card-title>
          Provider Performance Adjustment
          <v-spacer />
          <v-text-field
            v-on:input="debounceInput"
            :value="search"
            clearable
            prepend-icon="search"
            label="Search"
            single-line
            hide-details
          />
        </v-card-title>
        <v-data-table
          :options.sync="pagination"
          :footer-props="{ 'items-per-page-options': rowsPerPageItems }"
          :headers="headers"
          :items="providers"
          :server-items-length="totalProviders"
          :loading="loading"
          item-key="id"
          class="elevation-1 full-width full-height"
        >
          <v-progress-linear v-slot:progress color="blue" indeterminate />
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td class="text-center">
                  {{ item.provider_profile_id }}
                </td>
                <td class="text-center">
                  {{ item.fullName }}
                </td>
                <td
                  class="text-center font-weight-bold"
                  :class="item.proposed_change > 0 ? 'green--text' : 'red--text'"
                >
                  {{ item.proposed_change > 0 ? '+' + item.proposed_change : item.proposed_change }} point
                </td>
                <td class="text-center">
                  {{ item.reason.average_rating || 0 }}
                  <v-icon small color="amber">mdi mdi-star</v-icon>
                </td>
                <td class="text-center">
                  {{ formatHour(item.reason.online_schedule_open_hour) || 0 }}
                </td>
                <td class="text-center">
                  {{ item.reason.late_visit_note_appointments.length || 0 }}
                </td>
                <td class="text-center">
                  {{ item.reason.late_appointments.length || 0 }}
                </td>
                <td class="text-center">
                  {{ item.reason.cancelled_by_provider_appointments.length || 0 }}
                </td>
                <td class="text-center">{{ formatDateShort(item.evaluation_start) }}</td>
                <td class="px-0 py-1 text-center">
                  <v-btn @click.stop="openDialog(item)" color="secondary">View</v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </template>
    </v-layout>
    <v-dialog v-model="dialog" max-width="600">
      <v-card v-if="selectedItem" class="px-5 py-5">
        <v-card-text class="d-flex flex-row justify-content-between pb-0">
          <v-avatar :size="70" color="grey lighten-4" class="mr-5 my-auto">
            <img :src="selectedItem.avatar" class="cover" alt="provider avatar" />
          </v-avatar>
          <div class="d-flex flex-column my-auto">
            <v-container>
              <v-row>
                <p class="text-h5 mb-0">{{ selectedItem.fullName }}</p>
                <p class="my-auto ml-2 px-2">
                  {{ selectedItem.reason.average_rating || 0 }}
                  <v-icon small color="amber">mdi mdi-star</v-icon>
                </p>
              </v-row>
            </v-container>
            <a v-if="selectedItem.user.email" :href="`mailto:${selectedItem.user.email}`">
              {{ selectedItem.user.email }}
            </a>
            <p v-if="selectedItem.user.tel">({{ selectedItem.user.tel }})</p>
          </div>
          <div class="flex-grow-1 d-flex flex-column align-end my-auto">
            <p class="mb-0">Performance</p>
            <div class="d-flex flex-row">
              <p class="bg px-2 rounded mb-0 mr-2">
                {{ selectedItem.current_performance }}
              </p>
              <p
                class="rounded mb-0 font-weight-bold"
                :class="selectedItem.proposed_change > 0 ? 'green--text' : 'red--text'"
              >
                {{
                  selectedItem.proposed_change > 0 ? '+' + selectedItem.proposed_change : selectedItem.proposed_change
                }}
                <v-icon small :color="selectedItem.proposed_change > 0 ? 'green' : 'red'">
                  {{ selectedItem.proposed_change > 0 ? 'mdi mdi-arrow-up-bold' : 'mdi mdi-arrow-down-bold' }}
                </v-icon>
              </p>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-text class="mt-2">
          <v-container>
            <v-row justify="space-between">
              <p>Evaluation Date</p>
              <div>
                <span>
                  {{ formatDateShort(selectedItem.evaluation_start) }}
                </span>
                <v-icon class="my-auto mx-3" small>mdi mdi-arrow-left-right</v-icon>
                <span>
                  {{ formatDateShort(selectedItem.evaluation_end) }}
                </span>
              </div>
            </v-row>
            <v-row justify="space-between">
              <p>Open Hour</p>
              <p>{{ formatHour(selectedItem.reason.online_schedule_open_hour) }}</p>
            </v-row>
            <v-row justify="space-between">
              <p>Send Visit Note Late</p>
              <p>{{ selectedItem.reason.late_visit_note_appointments.length || 0 }}</p>
            </v-row>
            <v-row justify="space-between">
              <p>Total Appointment</p>
              <p>{{ selectedItem.reason.total_appointment || 0 }}</p>
            </v-row>
            <v-row justify="space-between">
              <p class="mb-2">Lated Appointment ({{ selectedItem.reason.late_appointments.length || 0 }})</p>
              <p class="mb-2">
                {{
                  (
                    (selectedItem.reason.late_appointments.length / selectedItem.reason.total_appointment) *
                    100
                  ).toFixed(0) || 0
                }}%
              </p>
            </v-row>
            <v-row
              v-if="selectedItem.reason.late_appointments"
              v-for="appoint in selectedItem.reason.late_appointments"
              :key="appoint.id"
              justify="space-between"
              class="px-3 py-1 rounded grey lighten-3 my-1"
            >
              <p class="mb-0 blue--text">#{{ appoint.id }}</p>
              <p class="mb-0">{{ appoint.patient_display_name }}</p>
              <p class="mb-0">
                {{ formatDateFull(appoint.appointed_at) }}
              </p>
              <p class="mb-0">
                {{ appoint.status }}
              </p>
            </v-row>
            <v-row justify="space-between">
              <p class="mb-2">
                Cancelled Appointment ({{ selectedItem.reason.cancelled_by_provider_appointments.length || 0 }})
              </p>
              <p class="mb-2">
                {{
                  (
                    (selectedItem.reason.cancelled_by_provider_appointments.length /
                      selectedItem.reason.total_appointment) *
                    100
                  ).toFixed(0) || 0
                }}%
              </p>
            </v-row>
            <v-row
              v-if="selectedItem.reason.cancelled_by_provider_appointments"
              v-for="appoint in selectedItem.reason.cancelled_by_provider_appointments"
              :key="appoint.id"
              justify="space-between"
              class="px-3 py-1 rounded grey lighten-3 my-1"
            >
              <p class="mb-0 blue--text">#{{ appoint.id }}</p>
              <p class="mb-0">{{ appoint.patient_display_name }}</p>
              <p class="mb-0">
                {{ formatDateFull(appoint.appointed_at) }}
              </p>
              <p class="mb-0">
                {{ appoint.status }}
              </p>
            </v-row>
            <v-row justify="center">
              <v-btn class="mr-3" @click.stop="rejectAdjustment(selectedItem.id)" color="error">Reject</v-btn>
              <v-btn @click.stop="approveAdjustment(selectedItem.id)" color="secondary">Approve</v-btn>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
/*eslint-disable no-unused-vars*/
import { Vue } from 'vue-property-decorator';
import providerPerformance from '../../../services/providerPerformance';
import store from '../../../store';
import tableMixin from '../../../Mixins/table';
import { debounce } from 'lodash';
import moment from 'moment-timezone';

export default Vue.extend({
  store,
  mixins: [tableMixin],
  data: () => ({
    loading: false,
    search: '',
    dialog: false,
    selectedItem: null,
    providers: [],
    totalProviders: 0,
    pagination: {},
    headers: [
      {
        text: 'Provider ID',
        align: 'center',
        value: 'provider_profile_id',
        sortable: true,
      },
      {
        text: 'Name',
        align: 'center',
        value: 'fullName',
        sortable: false,
      },
      {
        text: 'Proposed Change',
        align: 'center',
        sortable: false,
      },
      {
        text: 'Rating',
        align: 'center',
        sortable: false,
      },
      {
        text: 'Online Hour',
        align: 'center',
        sortable: false,
      },
      {
        text: 'Lated Visit Note',
        align: 'center',
        sortable: false,
      },
      {
        text: 'Lated Appointment',
        align: 'center',
        sortable: false,
      },
      {
        text: 'Cancelled Appointment',
        align: 'center',
        sortable: false,
      },
      {
        text: 'Evaluation Date',
        align: 'center',
        value: 'evaluation_start',
        sortable: true,
      },
      {
        text: 'Action',
        align: 'center',
        value: 'id',
        sortable: false,
      },
    ],
  }),
  computed: {
    user() {
      return store.state.auth.user;
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getAdjustmentList().then((data) => {
          this.providers = data.items;
          this.totalProviders = data.total;
        });
      },
      deep: true,
    },
    search() {
      this.getAdjustmentList().then((data) => {
        this.providers = data.items;
        this.totalProviders = data.total;
      });
    },
  },
  created() {},
  mounted() {
    this.getAdjustmentList().then((data) => {
      this.providers = data.items;
      this.totalProviders = data.total;
    });
  },
  methods: {
    openDialog(item) {
      this.selectedItem = item;
      this.dialog = true;
    },
    formatDateFull(date) {
      if (!date) return;
      const stillUtc = moment.tz(date, 'Asia/Bangkok');
      return stillUtc.format('DD/MM/YYYY HH:mm');
    },
    formatDateShort(date) {
      if (!date) return;
      return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
    },
    formatHour(hour) {
      if (hour == null || isNaN(hour)) return '';
      const h = Math.floor(hour);
      const m = Math.round((hour - h) * 60);
      return `${h} h ${m} min`;
    },
    approveAdjustment(adjustId) {
      this.$root
        .$confirm('Approve', `Are you sure for approve adjustment?`, {
          color: 'red',
          submitText: 'Approve',
        })
        .then((confirm) => {
          if (confirm) {
            this.loading = true;
            this.dialog = false;
            providerPerformance.approveAdjustment(adjustId).then(() => {
              this.$notify({
                title: 'Approve',
                text: `Approve success !`,
                type: 'success',
              });
              this.getAdjustmentList().then((data) => {
                this.providers = data.items;
                this.totalProviders = data.total;
                this.loading = false;
              });
            });
          }
        });
    },
    rejectAdjustment(adjustId) {
      this.$root
        .$confirm('Reject', `Are you sure for reject adjustment?`, {
          color: 'red',
          submitText: 'Approve',
        })
        .then((confirm) => {
          if (confirm) {
            this.loading = true;
            this.dialog = false;
            providerPerformance.rejectAdjustment(adjustId).then(() => {
              this.$notify({
                title: 'Reject',
                text: `Reject success !`,
                type: 'success',
              });
              this.getAdjustmentList().then((data) => {
                this.providers = data.items;
                this.totalProviders = data.total;
                this.loading = false;
              });
            });
          }
        });
    },
    getAdjustmentList() {
      this.loading = true;
      return new Promise((resolve, reject) => {
        const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;

        let query = {};
        query.archived = false;
        if (itemsPerPage !== -1) {
          query.per_page = itemsPerPage;
          query.page = page;
        } else {
          if (this.totalProviders) {
            query.per_page = this.totalProviders;
            query.page = 1;
          }
        }

        if (sortBy.length > 0) {
          // Support only one field sort
          query.order = sortBy[0];
          query.sort = sortDesc[0] ? 'desc' : 'asc';
        }

        if (this.search) query.q = this.search;

        providerPerformance
          .all(query)
          .then((data) => {
            const items = data.data;
            const total = data.pagination.total;
            this.loading = false;
            resolve({
              items,
              total,
            });
          })
          .catch((err) => {
            this.loading = false;
            reject(err);
          });
      });
    },
    debounceInput: debounce(function (e) {
      this.search = e;
    }, 500),
  },
  components: {},
});
</script>

<style scoped>
.bg {
  background-color: #42a5f5;
  color: white;
}
</style>
